import React, { useEffect, useState } from "react";
import axios from "../../auth/auth";
import { FiEdit, FiTrash2, FiSearch } from "react-icons/fi";
import { FaMapMarkerAlt } from "react-icons/fa";

const emptyQuotation = {
    quotation_number: "",
    customer_id: "",
    garage_id: "",
    service_start_date: "",
    tax_rate: 0,
    shipping_charge: 0,
    items: [],
    subscriptions: [],
    terms: []
};

const emptyItem = {
    master_item_id: "",
    item_name: "",
    description: "",
    quantity: "1",
    unit_price: ""
};

const emptySubscription = {
    master_subscription_id: "",
    item_name: "",
    description: "",
    monthly_price: "",
    billing_unit: ""
};

const formatDateTime = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    const formattedDate = date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric"
    });

    const formattedTime = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true
    });

    return `${formattedDate} at ${formattedTime}`;
};


export default function QuotationTab() {

    const BASE_URL = process.env.REACT_APP_API_BASE_URL;

    const [view, setView] = useState("cards");
    const [quotations, setQuotations] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [garages, setGarages] = useState([]);
    const [termsMaster, setTermsMaster] = useState([]);
    const [formOpen, setFormOpen] = useState(false);
    const [formData, setFormData] = useState(emptyQuotation);
    const [filteredGarages, setFilteredGarages] = useState([]);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [capitalItemsMaster, setCapitalItemsMaster] = useState([]);
    const [subscriptionItemsMaster, setSubscriptionItemsMaster] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [customerFilter, setCustomerFilter] = useState("All");
    const [tableCustomerId, setTableCustomerId] = useState("All");
    const [statusFilter, setStatusFilter] = useState("All");
    const [editingId, setEditingId] = useState(null);
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState("");

    // LOAD INITIAL DATA

    useEffect(() => {
        loadQuotations();
        loadCustomers();
        loadGarages();
        loadTerms();
        loadCapitalItemsMaster();
        // loadSubscriptionItemsMaster();
    }, []);

    const loadQuotations = async () => {
        try {
            const res = await axios.get(`${BASE_URL}/quotations/`);
            setQuotations(res.data || []);
        } catch {
            setQuotations([]);
        }
    };

    const loadCustomers = async () => {
        const res = await axios.get(`${BASE_URL}/customers/`);
        setCustomers(res.data || []);
    };

    const loadGarages = async () => {
        const res = await axios.get(`${BASE_URL}/garages/`);
        setGarages(res.data || []);
    };

    const loadTerms = async () => {
        try {
            const res = await axios.get(`${BASE_URL}/quotations/terms/master`);
            setTermsMaster(res.data || []);
        } catch {
            setTermsMaster([]);
        }
    };

    const calculateSubscriptionTotal = () => {
        return formData.subscriptions.reduce(
            (sum, s) => sum + Number(s.monthly_price || 0),
            0
        );
    };

    const loadCapitalItemsMaster = async () => {
        try {
            const res = await axios.get(`${BASE_URL}/quotations/master/capital-items`);
            setCapitalItemsMaster(res.data || []);
        } catch {
            setCapitalItemsMaster([]);
        }
    };

    const loadSubscriptionItemsMaster = async (customerId, garageId) => {
        if (!customerId || !garageId) return;
        try {
            const res = await axios.get(
                `${BASE_URL}/quotations/master/subscription-items`,
                {
                    params: {
                        customer_id: customerId,
                        garage_id: garageId
                    }
                }
            );
            setSubscriptionItemsMaster(res.data || []);
        } catch (err) {
            console.error("Subscription fetch error", err);
            setSubscriptionItemsMaster([]);
        }
    };

    // CUSTOMER CHANGE → FILTER GARAGES

    const handleCustomerChange = (customerId) => {
        const filtered = garages.filter(
            g => g.CustomerID === customerId
        );

        setFilteredGarages(filtered);

        setSubscriptionItemsMaster([]); // reset

        setFormData({
            ...formData,
            customer_id: customerId,
            garage_id: ""
        });
    };

    const handleGarageChange = (garageId) => {

        const updatedForm = {
            ...formData,
            garage_id: garageId
        };

        setFormData(updatedForm);

        loadSubscriptionItemsMaster(
            updatedForm.customer_id,
            garageId
        );
    };

    // ADD ITEMS

    const addItem = () => {
        setFormData({
            ...formData,
            items: [...formData.items, { ...emptyItem }]
        });
    };

    const updateItem = (index, field, value) => {

        const updated = [...formData.items];
        updated[index][field] = value;

        setFormData({
            ...formData,
            items: updated
        });
    };

    const removeItem = (index) => {

        const updated = [...formData.items];
        updated.splice(index, 1);

        setFormData({
            ...formData,
            items: updated
        });
    };

    // ADD SUBSCRIPTIONS

    const addSubscription = () => {

        setFormData({
            ...formData,
            subscriptions: [
                ...formData.subscriptions,
                { ...emptySubscription }
            ]
        });
    };

    const updateSubscription = (index, field, value) => {

        const updated = [...formData.subscriptions];
        updated[index][field] = value;

        setFormData({
            ...formData,
            subscriptions: updated
        });
    };

    const removeSubscription = (index) => {

        const updated = [...formData.subscriptions];
        updated.splice(index, 1);

        setFormData({
            ...formData,
            subscriptions: updated
        });
    };


    const updateTerm = (index, value) => {

        const updated = [...formData.terms];
        updated[index].content = value;

        setFormData({
            ...formData,
            terms: updated
        });
    };

    // SAVE QUOTATION

    const handleSave = async () => {

        if (!formData.customer_id || !formData.garage_id) {
            alert("Customer and Garage required");
            return;
        }

        const payload = {
            customer_id: formData.customer_id,
            garage_id: formData.garage_id,
            service_start_date: formData.service_start_date || null,
            tax_rate: Number(formData.tax_rate || 0),
            shipping_charge: Number(formData.shipping_charge || 0),
            items: formData.items,
            subscriptions: formData.subscriptions,
            terms: formData.terms
        };

        try {

            if (editingId) {
                // 🔵 UPDATE DRAFT
                await axios.put(
                    `${BASE_URL}/quotations/${editingId}`,
                    payload
                );
            } else {
                // 🟢 CREATE NEW
                await axios.post(
                    `${BASE_URL}/quotations/create`,
                    payload
                );
            }

            setEditingId(null);
            setFormOpen(false);
            loadQuotations();

        } catch (err) {
            alert("Save failed");
        }
    };

    // OPEN FORM

    const openForm = () => {

        if (!termsMaster.length) {
            alert("Terms are still loading. Please wait.");
            return;
        }

        const mappedTerms = termsMaster.map((t, index) => ({
            title: t.title,
            content: t.content,
            display_order: t.display_order ?? index
        }));

        setEditingId(null);

        setFormData({
            ...JSON.parse(JSON.stringify(emptyQuotation)),
            terms: mappedTerms
        });

        setFormOpen(true);
    };

    // DELETE

    const deleteQuotation = async (id) => {

        if (!window.confirm("Delete quotation?")) return;

        await axios.delete(
            `${BASE_URL}/quotations/${id}`
        );

        loadQuotations();
    };

    const cloneQuotation = async (quotationId) => {
        try {

            const res = await axios.post(
                `${BASE_URL}/quotations/${quotationId}/clone`
            );

            const newId = res.data.new_quotation_id;

            alert("New revision created");

            setPreviewOpen(false);
            loadQuotations();

        } catch (err) {
            alert(err.response?.data?.detail || "Clone failed");
        }
    };

    const editDraftQuotation = async (quotationId) => {
        try {

            const res = await axios.get(
                `${BASE_URL}/quotations/${quotationId}`
            );

            const data = res.data;

            setEditingId(quotationId);

            setFormData({
                quotation_number: data.quotation_number,
                customer_id: data.customer_id,
                garage_id: data.garage_id,
                service_start_date: data.service_start_date || "",
                tax_rate: Number(data.tax_rate || 0),
                shipping_charge: Number(data.shipping_charge || 0),

                items: data.items.map(i => ({
                    master_item_id: i.master_item_id,
                    item_name: i.item_name,
                    description: i.description,
                    quantity: i.quantity,
                    unit_price: Number(i.unit_price)
                })),

                subscriptions: data.subscriptions.map(s => ({
                    master_subscription_id: s.master_subscription_id,
                    item_name: s.item_name,
                    description: s.description,
                    monthly_price: Number(s.monthly_price),
                    billing_unit: s.billing_unit
                })),

                terms: data.terms
            });

            const filtered = garages.filter(
                g => g.GarageID === data.garage_id
            );

            setFilteredGarages(filtered);

            setFormOpen(true);

        } catch {
            alert("Cannot edit quotation");
        }
    };

    const openPreview = async (id) => {
        try {
            const res = await axios.get(`${BASE_URL}/quotations/${id}`);
            setPreviewData(res.data);
            setPreviewOpen(true);
        } catch (err) {
            console.error(err);
            alert("Error loading quotation details");
        }
    };

    // CALCULATE TOTAL

    const calculateCapitalTotal = () => {

        return formData.items.reduce(
            (sum, i) =>
                sum + (i.quantity * i.unit_price),
            0
        );
    };

    // CAPITAL subtotal only
    const calculateCapitalSubtotal = () => {
        return calculateCapitalTotal();
    };

    // TAX applies only to capital
    const calculateTaxAmount = () => {
        return (
            calculateCapitalSubtotal() *
            (Number(formData.tax_rate || 0) / 100)
        );
    };

    // Estimated total = capital + tax + shipping
    const calculateEstimatedTotal = () => {
        return (
            calculateCapitalSubtotal() +
            calculateTaxAmount() +
            Number(formData.shipping_charge || 0)
        );
    };

    const filteredQuotations = quotations.filter(q => {

        const customer = customers.find(
            c => c.CustomerID === q.customer_id
        );

        const customerName = customer?.CustomerName || "";

        const matchesSearch =
            !searchTerm ||
            q.quotation_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            customerName.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesCustomer =
            customerFilter === "All" || q.customer_id === customerFilter;

        const matchesStatus =
            statusFilter === "All" || q.status === statusFilter;

        return matchesSearch && matchesCustomer && matchesStatus;

    });

    const customerIdsWithQuotations = new Set(quotations.map(q => q.customer_id));
    const customersWithQuotations = customers.filter(c =>
        customerIdsWithQuotations.has(c.CustomerID)
    );

    const tableQuotations = quotations.filter(q =>
        tableCustomerId === "All" || q.customer_id === tableCustomerId
    );

    const updateQuotationStatus = async (status, reason = null) => {
        try {

            await axios.put(
                `${BASE_URL}/quotations/${previewData.id}/status`,
                null,
                {
                    params: {
                        status,
                        rejection_reason: reason
                    }
                }
            );

            alert(`Quotation ${status}`);
            setPreviewOpen(false);
            setRejectModalOpen(false);
            setRejectReason("");
            loadQuotations();

        } catch (err) {
            alert(err.response?.data?.detail || "Error updating quotation status");
        }
    };

    const downloadPdf = async (quotationId) => {
        try {

            const response = await axios.get(
                `${BASE_URL}/quotations/${quotationId}/download`,
                { responseType: "blob" }
            );

            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = `quotation_${quotationId}.pdf`;
            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);

        } catch (err) {
            alert("Error downloading PDF");
        }
    };

    // UI

    return (
        <div className="p-6">

            {/* TOP BAR */}

            <div className="flex justify-between mb-4">
                <div className="flex gap-3">

                    {/* SEARCH */}
                    <div className="relative w-72">
                        <FiSearch className="absolute left-3 top-3 text-gray-400" />
                        <input
                            className="w-full pl-10 pr-4 py-2 border rounded-lg"
                            placeholder="Search quotations..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* CUSTOMER FILTER */}
                    <select
                        className="border px-4 py-2 rounded-lg"
                        value={customerFilter}
                        onChange={(e) => setCustomerFilter(e.target.value)}
                    >
                        <option value="All">All Customers</option>
                        {customersWithQuotations.map((c) => (
                            <option key={c.CustomerID} value={c.CustomerID}>
                                {c.CustomerName}
                            </option>
                        ))}
                    </select>

                    {/* STATUS FILTER DROPDOWN */}
                    <select
                        className="border px-4 py-2 rounded-lg"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="All">All</option>
                        <option value="Draft">Draft</option>
                        <option value="Sent">Sent</option>
                        <option value="Approved">Approved</option>
                        <option value="Rejected">Rejected</option>
                        <option value="Superseded">Superseded</option>
                    </select>

                </div>

                <div className="flex gap-3">
                    <button
                        onClick={() => setView(view === "cards" ? "table" : "cards")}
                        className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg"
                    >
                        {view === "cards" ? "Check Status" : "Cards View"}
                    </button>

                    <button
                        onClick={openForm}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg"
                    >
                        + New Quotation
                    </button>
                </div>
            </div>

            {/* CARDS */}

            {view === "cards" && (

                <div className="grid grid-cols-3 gap-4">

                    {filteredQuotations.map((q) => (

                        <div
                            key={q.id}
                            onClick={() => openPreview(q.id)}
                            className="bg-white border border-gray-100 shadow-sm hover:shadow-md p-6 rounded-xl cursor-pointer transition-all duration-200"
                        >
                            <div className="flex justify-between items-start">
                                <div>
                                    {(() => {
                                        const customer = customers.find(
                                            c => c.CustomerID === q.customer_id
                                        );

                                        return (
                                            <>
                                                <h2 className="text-lg font-semibold text-gray-800">
                                                    {customer?.CustomerName}
                                                </h2>

                                                <p className="text-sm text-gray-500 flex items-center gap-2 truncate">
                                                    <FaMapMarkerAlt className="text-gray-400 text-sm shrink-0" />
                                                    <span className="truncate">{customer?.Address}</span>
                                                </p>

                                                <p className="text-sm text-gray-400"> Created on {" "}
                                                    {formatDateTime(q.created_at)}
                                                </p>
                                            </>
                                        );
                                    })()}
                                </div>

                                <span
                                    className={`text-xs font-semibold px-3 py-1 rounded-full
                ${q.status === "Approved"
                                            ? "bg-green-100 text-green-700"
                                            : q.status === "Rejected"
                                                ? "bg-red-100 text-red-700"
                                                : q.status === "Sent"
                                                    ? "bg-blue-100 text-blue-700"
                                                    : "bg-yellow-100 text-yellow-700"
                                        }`}
                                >
                                    {q.status === "Approved" ? "Accepted" : q.status}
                                </span>
                            </div>

                            <div className="my-4">
                                <p className="text-sm text-gray-400">Estimated Total</p>
                                <h3 className="text-lg font-semibold text-gray-800">
                                    ${Number(q.estimated_total || 0).toFixed(2)}
                                </h3>
                            </div>

                            <hr className="my-4" />

                            <div className="flex justify-end gap-3">
                                {q.status === "Draft" && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            editDraftQuotation(q.id);
                                        }}
                                        className="px-4 py-1 rounded bg-gray-100 flex items-center gap-1 hover:bg-gray-200 transition"
                                    >
                                        <FiEdit /> Edit
                                    </button>
                                )}

                                {q.status === "Rejected" && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            cloneQuotation(q.id);
                                        }}
                                        className="px-4 py-1 rounded bg-gray-100 flex items-center gap-1 hover:bg-gray-200 transition"
                                    >
                                        <FiEdit /> Revise
                                    </button>
                                )}

                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        deleteQuotation(q.id);
                                    }}
                                    className="px-4 py-1 rounded bg-red-100 text-red-600 flex items-center gap-1 hover:bg-red-200 transition"
                                >
                                    <FiTrash2 /> Delete
                                </button>
                            </div>
                        </div>

                    ))}
                </div>
            )}

            {/* TABLE VIEW */}
            {view === "table" && (
                <div className="bg-white border border-gray-100 rounded-lg p-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                        <div className="flex items-center gap-3">
                            <label className="text-sm font-medium">Customer:</label>
                            <select
                                className="border px-3 py-2 rounded-lg"
                                value={tableCustomerId}
                                onChange={(e) => setTableCustomerId(e.target.value)}
                            >
                                <option value="All">Select Customer</option>
                                {customersWithQuotations.map((c) => (
                                    <option key={c.CustomerID} value={c.CustomerID}>
                                        {c.CustomerName}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <button
                            onClick={() => setView("cards")}
                            className="border px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200"
                        >
                            Back to Cards
                        </button>
                    </div>

                    {tableCustomerId === "All" ? (
                        <p className="text-sm text-gray-500">
                            Select a customer to view their quotation status.
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm text-left">
                                <thead>
                                    <tr className="border-b bg-gray-50">
                                        <th className="px-4 py-2">Quotation #</th>
                                        <th className="px-4 py-2">Created</th>
                                        <th className="px-4 py-2">Status</th>
                                        <th className="px-4 py-2 text-right">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tableQuotations.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="px-4 py-6 text-center text-gray-500">
                                                No quotations found for this customer.
                                            </td>
                                        </tr>
                                    ) : (
                                        tableQuotations.map((q) => (
                                            <tr
                                                key={q.id}
                                                className="border-b hover:bg-gray-50 cursor-pointer"
                                                onClick={() => openPreview(q.id)}
                                            >
                                                <td className="px-4 py-2">
                                                    {q.quotation_number || "-"}
                                                </td>
                                                <td className="px-4 py-2">
                                                    {formatDateTime(q.created_at)}
                                                </td>
                                                <td className="px-4 py-2">
                                                    {q.status}
                                                </td>
                                                <td className="px-4 py-2 text-right">
                                                    ${Number(q.estimated_total || 0).toFixed(2)}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* FORM */}

            {formOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center">
                    <div className="bg-white w-[1000px] p-6 rounded-xl max-h-[90vh] overflow-y-auto">

                        <h2 className="text-xl font-semibold mb-4">
                            New Quotation
                        </h2>

                        <div className="grid grid-cols-2 gap-4 mb-6">

                            {/* CUSTOMER SELECT */}
                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    Customer
                                </label>
                                <select
                                    className="border p-2 w-full rounded"
                                    value={formData.customer_id}
                                    onChange={(e) => handleCustomerChange(e.target.value)}
                                >
                                    <option value="">Select Customer</option>
                                    {customers.map((c) => (
                                        <option key={c.CustomerID} value={c.CustomerID}>
                                            {c.CustomerName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* GARAGE SELECT */}
                            {/* GARAGE SELECT */}
                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    Garage
                                </label>

                                <select
                                    className="border p-2 w-full rounded"
                                    value={formData.garage_id}
                                    onChange={(e) => {

                                        const garageId = e.target.value;

                                        setFormData(prev => ({
                                            ...prev,
                                            garage_id: garageId
                                        }));

                                        // 🔵 LOAD SUBSCRIPTIONS FOR SELECTED GARAGE
                                        loadSubscriptionItemsMaster(
                                            formData.customer_id,
                                            garageId
                                        );

                                    }}
                                >
                                    <option value="">Select Garage</option>

                                    {filteredGarages.map((g) => (
                                        <option key={g.GarageID} value={g.GarageID}>
                                            {g.GarageName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* CAPITAL ITEMS */}
                        <h3 className="font-semibold mb-2">List of Items</h3>
                        <div className="grid grid-cols-7 gap-4 mb-4 items-center">
                            <div className="col-span-2">
                                <label className="block text-sm font-medium mb-1">Name</label>
                            </div>
                            <div className="col-span-2">
                                <label className="block text-sm font-medium mb-1">Item Description</label>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Quantity</label>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Unit Price ($)</label>
                            </div>
                            <div></div> {/* For remove button */}
                            {formData.items.map((item, i) => (
                                <div key={i} className="contents">
                                    <select
                                        className="border p-2 rounded col-span-2"
                                        value={item.master_item_id || ""}
                                        onChange={(e) => {

                                            const selectedId = Number(e.target.value);

                                            const master = capitalItemsMaster.find(
                                                m => m.id === selectedId
                                            );

                                            const updated = [...formData.items];

                                            updated[i] = {
                                                ...updated[i],
                                                master_item_id: selectedId,
                                                item_name: master?.item_name || "",
                                                description: master?.description || "",
                                                unit_price: master?.default_unit_price || 0
                                            };

                                            setFormData({
                                                ...formData,
                                                items: updated
                                            });
                                        }}
                                    >
                                        <option value="">Select Item</option>

                                        {capitalItemsMaster
                                            .filter(m => {
                                                const selectedIds = formData.items
                                                    .map(item => Number(item.master_item_id))
                                                    .filter(Boolean);

                                                return (
                                                    !selectedIds.includes(m.id) ||
                                                    m.id === Number(item.master_item_id)
                                                );
                                            })
                                            .map(m => (
                                                <option key={m.id} value={m.id}>
                                                    {m.item_name}
                                                </option>
                                            ))}
                                    </select>

                                    <input
                                        type="text"
                                        className="border p-2 rounded col-span-2"
                                        placeholder="Description"
                                        value={item.description}
                                        onChange={(e) => updateItem(i, "description", e.target.value)}
                                    />

                                    <input
                                        type="number"
                                        min={0}
                                        className="border p-2 rounded"
                                        placeholder="Qty"
                                        value={item.quantity}
                                        onChange={(e) => updateItem(i, "quantity", e.target.value)}
                                    />
                                    <input
                                        type="number"
                                        min={0}
                                        className="border p-2 rounded bg-gray-50"
                                        placeholder="Price"
                                        value={item.unit_price}
                                        readOnly
                                    />
                                    <button
                                        type="button"
                                        className="h-[42px] w-[42px] flex items-center justify-center col-span-1
                                            border border-red-200 text-red-600 
                                            rounded hover:bg-red-50 transition"
                                        onClick={() => removeItem(i)}
                                    >
                                        <FiTrash2 />
                                    </button>
                                </div>
                            ))}
                        </div>

                        <button
                            onClick={addItem}
                            className="mb-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                        >
                            + Add Item
                        </button>

                        <p className="mb-6 font-semibold">
                            Total: ${calculateCapitalTotal().toFixed(2)}
                        </p>

                        {/* SUBSCRIPTION ITEMS */}
                        <h3 className="font-semibold mb-2">Subscription Items (Monthly)</h3>
                        <div className="grid grid-cols-4 gap-4 mb-4 items-center">
                            <div>
                                <label className="block text-sm font-medium mb-1">Name</label>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Monthly Price ($)</label>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Billing Unit</label>
                            </div>
                            <div></div> {/* For remove button */}
                            {formData.subscriptions.map((sub, i) => (
                                <div key={i} className="contents">
                                    <select
                                        className="border p-2 rounded"
                                        value={sub.master_subscription_id || ""}
                                        onChange={(e) => {

                                            const selectedId = Number(e.target.value);

                                            const master = subscriptionItemsMaster.find(
                                                m => m.master_subscription_id === selectedId
                                            );

                                            const updated = [...formData.subscriptions];

                                            updated[i] = {
                                                ...updated[i],
                                                master_subscription_id: selectedId,
                                                item_name: master?.item_name || "",
                                                monthly_price: master?.monthly_price || 0,
                                                billing_unit: master?.billing_unit || ""
                                            };

                                            setFormData({
                                                ...formData,
                                                subscriptions: updated
                                            });
                                        }}
                                    >
                                        <option value="">Select Subscription</option>

                                        {subscriptionItemsMaster
                                            .filter(m => {
                                                const selectedIds = formData.subscriptions
                                                    .map(sub => Number(sub.master_subscription_id))
                                                    .filter(Boolean);

                                                return (
                                                    !selectedIds.includes(m.id) ||
                                                    m.master_subscription_id === Number(sub.master_subscription_id)
                                                );
                                            })
                                            .map(m => (
                                                <option key={m.master_subscription_id} value={m.master_subscription_id}>
                                                    {m.item_name}
                                                </option>
                                            ))}
                                    </select>

                                    <input
                                        type="number"
                                        min={0}
                                        className="border p-2 rounded"
                                        placeholder="Monthly Price"
                                        value={sub.monthly_price}
                                        onChange={(e) =>
                                            updateSubscription(i, "monthly_price", e.target.value)
                                        }
                                    />
                                    <input
                                        type="text"
                                        className="border p-2 rounded"
                                        placeholder="Billing Unit (e.g. per garage)"
                                        value={sub.billing_unit}
                                        onChange={(e) =>
                                            updateSubscription(i, "billing_unit", e.target.value)
                                        }
                                    />
                                    <button
                                        type="button"
                                        className="h-[42px] w-[42px] flex items-center justify-center 
                                            border border-red-200 text-red-600 
                                            rounded hover:bg-red-50 transition"
                                        onClick={() => removeSubscription(i)}
                                    >
                                        <FiTrash2 />
                                    </button>
                                </div>
                            ))}
                        </div>

                        <button
                            onClick={addSubscription}
                            className="mb-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                        >
                            + Add Subscription
                        </button>

                        <div className="grid grid-cols-3 gap-4 mb-6">

                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    Tax Rate (%)
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    className="border p-2 rounded w-full"
                                    value={formData.tax_rate}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            tax_rate: e.target.value
                                        })
                                    }
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    Shipping Charge ($)
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    className="border p-2 rounded w-full"
                                    value={formData.shipping_charge}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            shipping_charge: e.target.value
                                        })
                                    }
                                />
                            </div>
                        </div>

                        <div className="mb-6 font-semibold space-y-1">
                            <p>Capital Subtotal: ${calculateCapitalSubtotal().toFixed(2)}</p>

                            <p>Tax: ${calculateTaxAmount().toFixed(2)}</p>

                            <p>Shipping: ${Number(formData.shipping_charge || 0).toFixed(2)}</p>

                            <p className="text-lg font-bold">
                                Estimated Total: ${calculateEstimatedTotal().toFixed(2)}
                            </p>
                        </div>

                        {formData.terms.map((t, i) => (
                            <div key={i} className="mb-4">
                                <label className="block font-semibold mb-1">
                                    {i === 0
                                        ? "Terms of Subscription and Termination:"
                                        : i === 1
                                            ? "Payment Terms:"
                                            : t.title}
                                </label>

                                <textarea
                                    className="border w-full p-2 rounded"
                                    value={t.content}
                                    onChange={(e) => updateTerm(i, e.target.value)}
                                    rows={4}
                                />
                            </div>
                        ))}

                        {/* SAVE */}

                        <div className="flex justify-end gap-3 mt-4">
                            <button
                                onClick={() => setFormOpen(false)}
                                className="bg-gray-200 px-4 py-2 rounded"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={handleSave}
                                className="bg-blue-600 text-white px-4 py-2 rounded"
                            >
                                Save Quotation
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* PREVIEW MODAL */}

            {previewOpen && previewData && (

                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                    <div className="bg-white w-[900px] max-h-[95vh] overflow-y-auto p-10 rounded-lg shadow-xl">

                        {/* HEADER */}

                        <div className="text-center mb-8">
                            <h2 className="text-lg font-semibold">
                                Subscription Services Order Form
                            </h2>

                            <p className="text-sm text-gray-600">
                                ParkEngage Inc.
                            </p>
                            <p className="text-sm text-gray-600">
                                4100 Moorpark Ave., Suite 203, San Jose, CA 95117
                            </p>
                        </div>

                        <p className="mt-2">
                            Status:
                            <span className={`ml-2 font-semibold ${previewData.status === "Approved"
                                ? "text-green-600"
                                : previewData.status === "Rejected"
                                    ? "text-red-600"
                                    : "text-yellow-600"
                                }`}>
                                {previewData.status}
                            </span>
                        </p>

                        {previewData.rejection_reason && (
                            <p className="mt-2 text-red-600">
                                <strong>Rejection Reason:</strong> {previewData.rejection_reason}
                            </p>
                        )}

                        {/* CUSTOMER INFO */}

                        <div className="mb-6">
                            <h3 className="font-semibold mb-2">
                                Customer Information:
                            </h3>

                            <p>
                                {customers.find(c => c.CustomerID === previewData.customer_id)?.CustomerName}
                            </p>

                            <p>
                                {customers.find(c => c.CustomerID === previewData.customer_id)?.Address}
                            </p>
                        </div>

                        {/* FACILITY INFO */}

                        <div className="mb-6">

                            <h3 className="font-semibold mb-2">
                                Facility Information:
                            </h3>

                            <table className="w-full border">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="border p-2 text-left">Facility Name</th>
                                        <th className="border p-2 text-left">Address</th>
                                        <th className="border p-2 text-center">Capacity</th>
                                        <th className="border p-2 text-center"># Entries</th>
                                        <th className="border p-2 text-center"># Exits</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {(() => {
                                        const garage = garages.find(
                                            g => g.GarageID === previewData.garage_id
                                        );

                                        if (!garage) return null;

                                        return (
                                            <tr>
                                                <td className="border p-2">
                                                    {garage.GarageName}
                                                </td>
                                                <td className="border p-2">
                                                    {garage.Address}
                                                </td>
                                                <td className="border p-2 text-center">
                                                    {garage.SizeSpaces ?? "-"}
                                                </td>
                                                <td className="border p-2 text-center">
                                                    {garage.EntryGates ?? garage.NumberOfEntries ?? "-"}
                                                </td>
                                                <td className="border p-2 text-center">
                                                    {garage.ExitGates ?? garage.NumberOfExits ?? "-"}
                                                </td>
                                            </tr>
                                        );
                                    })()}
                                </tbody>
                            </table>
                        </div>

                        {/* CAPITAL ITEMS */}

                        <h3 className="font-semibold mb-2">
                            Capital Expense
                        </h3>

                        <table className="w-full border mb-6">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="border p-2 text-left">Item</th>
                                    <th className="border p-2 text-right">Qty</th>
                                    <th className="border p-2 text-right">Unit Price</th>
                                    <th className="border p-2 text-right">Total</th>
                                </tr>
                            </thead>

                            <tbody>
                                {previewData.items?.map((item, i) => (
                                    <tr key={i}>
                                        <td className="border p-2">{item.item_name}</td>
                                        <td className="border p-2 text-right">{item.quantity}</td>
                                        <td className="border p-2 text-right">
                                            ${Number(item.unit_price).toFixed(2)}
                                        </td>
                                        <td className="border p-2 text-right">
                                            ${Number(item.total_price ?? item.quantity * item.unit_price).toFixed(2)}
                                        </td>
                                    </tr>
                                ))}

                                <tr className="font-semibold">
                                    <td colSpan="3" className="border p-2 text-right">
                                        Subtotal
                                    </td>
                                    <td className="border p-2 text-right">
                                        ${Number(previewData.total_capital_amount).toFixed(2)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        {/* SUMMARY SECTION */}

                        <table className="w-full border mb-6">
                            <tbody>

                                <tr>
                                    <td className="border p-2 text-right">
                                        Capital Subtotal
                                    </td>
                                    <td className="border p-2 text-right">
                                        ${Number(previewData.total_capital_amount).toFixed(2)}
                                    </td>
                                </tr>

                                <tr>
                                    <td className="border p-2 text-right">
                                        Sales Tax ({previewData.tax_rate}%)
                                    </td>
                                    <td className="border p-2 text-right">
                                        ${Number(previewData.tax_amount).toFixed(2)}
                                    </td>
                                </tr>

                                <tr>
                                    <td className="border p-2 text-right">
                                        Estimated Shipping
                                    </td>
                                    <td className="border p-2 text-right">
                                        ${Number(previewData.shipping_charge).toFixed(2)}
                                    </td>
                                </tr>

                                <tr className="font-bold text-lg">
                                    <td className="border p-2 text-right">
                                        Estimated Total
                                    </td>
                                    <td className="border p-2 text-right">
                                        ${Number(previewData.estimated_total).toFixed(2)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>


                        {/* SUBSCRIPTIONS */}
                        {previewData.subscriptions?.length > 0 && (
                            <>
                                <h3 className="font-semibold mb-2">
                                    Subscription Option
                                </h3>

                                <table className="w-full border mb-6">
                                    <thead className="bg-gray-100">
                                        <tr>
                                            <th className="border p-2 text-left">Item</th>
                                            <th className="border p-2 text-right">Monthly Price</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {previewData.subscriptions.map((sub, i) => (
                                            <tr key={i}>
                                                <td className="border p-2">{sub.item_name}</td>
                                                <td className="border p-2 text-right">
                                                    ${Number(sub.monthly_price).toFixed(2)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}

                        {/* SERVICE START */}

                        <div className="mb-6">
                            <strong>Service Start Date:</strong>{" "}
                        </div>

                        {/* SIGNATURE SECTION */}
                        <div className="mt-16 grid grid-cols-2 gap-16">

                            {/* LEFT SIDE - CUSTOMER REPRESENTATIVE */}
                            <div>
                                <p className="font-semibold underline mb-6">
                                    {previewData.customer_name}
                                </p>

                                {/* Signature Line */}
                                <div className="mb-6">
                                    <div className="border-b border-gray-600 h-6"></div>
                                    <p className="italic text-sm mt-1">Signature</p>
                                </div>

                                {/* Printed Name */}
                                <div className="mb-6">
                                    <p className="mb-1">
                                        {previewData.representative_name || ""}
                                    </p>
                                    <div className="border-b border-gray-600"></div>
                                    <p className="italic text-sm mt-1">Printed Name</p>
                                </div>

                                {/* Title */}
                                <div className="mb-6">
                                    <p className="mb-1">
                                        {previewData.representative_title || ""}
                                    </p>
                                    <div className="border-b border-gray-600"></div>
                                    <p className="italic text-sm mt-1">Title</p>
                                </div>

                                {/* Date */}
                                <div>
                                    <div className="border-b border-gray-600 h-6"></div>
                                    <p className="italic text-sm mt-1">Date</p>
                                </div>
                            </div>

                            {/* RIGHT SIDE - EMPTY */}
                            <div>
                                <p className="font-semibold underline mb-6">
                                    ParkEngage Inc.
                                </p>

                                {/* Signature */}
                                <div className="mb-6">
                                    <div className="border-b border-gray-600 h-6"></div>
                                    <p className="italic text-sm mt-1">Signature</p>
                                </div>

                                {/* Printed Name */}
                                <div className="mb-6">
                                    <div className="border-b border-gray-600 h-6"></div>
                                    <p className="italic text-sm mt-1">Printed Name</p>
                                </div>

                                {/* Title */}
                                <div className="mb-6">
                                    <div className="border-b border-gray-600 h-6"></div>
                                    <p className="italic text-sm mt-1">Title</p>
                                </div>

                                {/* Date */}
                                <div>
                                    <div className="border-b border-gray-600 h-6"></div>
                                    <p className="italic text-sm mt-1">Date</p>
                                </div>
                            </div>
                        </div>

                        {/* TERMS */}

                        <h3 className="font-semibold mb-2">
                            Terms and Conditions
                        </h3>

                        <div className="text-sm space-y-3 mb-6">

                            {previewData.terms
                                ?.sort((a, b) => a.display_order - b.display_order)
                                .map((term, i) => (

                                    <div key={i}>
                                        <strong>{term.title}</strong>
                                        <p>{term.content}</p>
                                    </div>
                                ))}
                        </div>

                        <div className="flex justify-between mt-6">

                            <div className="flex gap-3">

                                <button
                                    disabled={previewData.status === "Approved"}
                                    onClick={() => updateQuotationStatus("Approved")}
                                    className={`px-5 py-2 rounded text-white ${previewData.status === "Approved"
                                        ? "bg-gray-400 cursor-not-allowed"
                                        : "bg-green-600 hover:bg-green-700"
                                        }`}
                                >
                                    Accept Quotation
                                </button>

                                <button
                                    disabled={previewData.status === "Rejected"}
                                    onClick={() => setRejectModalOpen(true)}
                                    className={`px-5 py-2 rounded text-white ${previewData.status === "Rejected"
                                        ? "bg-gray-400 cursor-not-allowed"
                                        : "bg-red-600 hover:bg-red-700"
                                        }`}
                                >
                                    Reject Quotation
                                </button>
                            </div>

                            <div className="flex gap-3">

                                <button
                                    onClick={() => downloadPdf(previewData.id)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                                >
                                    Download PDF
                                </button>

                                <button
                                    onClick={() => setPreviewOpen(false)}
                                    className="bg-gray-300 px-4 py-2 rounded"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {rejectModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
                    <div className="bg-white w-[500px] p-6 rounded-lg">

                        <h3 className="text-lg font-semibold mb-4">
                            Reason for Rejection
                        </h3>

                        <textarea
                            className="border w-full p-2 rounded mb-4"
                            rows={4}
                            placeholder="Enter rejection reason..."
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                        />

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setRejectModalOpen(false);
                                    setRejectReason("");
                                }}
                                className="bg-gray-300 px-4 py-2 rounded"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={() => {
                                    if (!rejectReason.trim()) {
                                        alert("Rejection reason is required");
                                        return;
                                    }
                                    updateQuotationStatus("Rejected", rejectReason);
                                }}
                                className="bg-red-600 text-white px-4 py-2 rounded"
                            >
                                Confirm Rejection
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </div >
    );
}